import {
  CHEST,
  COBBLESTONE,
  CRAFTING_TABLE,
  FURNACE,
  GOLD_BLOCK,
  LOG,
  PLANKS,
  SAND,
  TNT,
  TORCH,
  COBWEB,
} from "./blocks";
import {
  ARROW,
  BOW,
  CHARCOAL,
  COAL,
  DIAMOND,
  DIAMOND_AXE,
  DIAMOND_BOOTS,
  DIAMOND_CHESTPLATE,
  DIAMOND_HELMET,
  DIAMOND_LEGGINGS,
  DIAMOND_PICKAXE,
  DIAMOND_SHOVEL,
  DIAMOND_SWORD,
  FEATHER,
  FLINT,
  GOLD_INGOT,
  GUNPOWDER,
  IRON_AXE,
  IRON_BOOTS,
  IRON_CHESTPLATE,
  IRON_HELMET,
  IRON_INGOT,
  IRON_LEGGINGS,
  IRON_PICKAXE,
  IRON_SHOVEL,
  IRON_SWORD,
  LEATHER,
  LEATHER_BOOTS,
  LEATHER_CHESTPLATE,
  LEATHER_HELMET,
  LEATHER_LEGGINGS,
  STICK,
  STONE_AXE,
  STONE_PICKAXE,
  STONE_SHOVEL,
  STONE_SWORD,
  STRING,
  WOODEN_AXE,
  WOODEN_PICKAXE,
  WOODEN_SHOVEL,
  WOODEN_SWORD,
  type ItemStack,
} from "./items";

/**
 * Crafting recipes.
 *
 * Shaped recipes use a row-major matrix of item ids (`null` = must be empty)
 * and match anywhere in the grid — empty rows and columns are trimmed on both
 * sides before comparing. Shapeless recipes compare the multiset of occupied
 * cells. Each occupied cell contributes one ingredient per craft.
 */

export interface Recipe {
  readonly id: string;
  readonly result: ItemStack;
  /** Shaped matrix; mutually exclusive with `ingredients`. */
  readonly pattern?: readonly (readonly (number | null)[])[];
  /** Shapeless ingredient ids, one per occupied cell. */
  readonly ingredients?: readonly number[];
}

export interface GridView {
  /** Grid is square; width * width cells, row-major. */
  readonly width: number;
  readonly cells: readonly (ItemStack | null)[];
}

export const RECIPES: readonly Recipe[] = [
  {
    id: "oak_planks",
    ingredients: [LOG],
    result: { id: PLANKS, count: 4 },
  },
  {
    id: "sticks",
    pattern: [[PLANKS], [PLANKS]],
    result: { id: STICK, count: 4 },
  },
  {
    id: "torches_from_coal",
    pattern: [[COAL], [STICK]],
    result: { id: TORCH, count: 4 },
  },
  {
    id: "torches_from_charcoal",
    pattern: [[CHARCOAL], [STICK]],
    result: { id: TORCH, count: 4 },
  },
  {
    id: "crafting_table",
    pattern: [
      [PLANKS, PLANKS],
      [PLANKS, PLANKS],
    ],
    result: { id: CRAFTING_TABLE, count: 1 },
  },
  {
    id: "furnace",
    pattern: [
      [COBBLESTONE, COBBLESTONE, COBBLESTONE],
      [COBBLESTONE, null, COBBLESTONE],
      [COBBLESTONE, COBBLESTONE, COBBLESTONE],
    ],
    result: { id: FURNACE, count: 1 },
  },
  {
    id: "chest",
    pattern: [
      [PLANKS, PLANKS, PLANKS],
      [PLANKS, null, PLANKS],
      [PLANKS, PLANKS, PLANKS],
    ],
    result: { id: CHEST, count: 1 },
  },
  {
    id: "gold_block",
    pattern: [
      [GOLD_INGOT, GOLD_INGOT, GOLD_INGOT],
      [GOLD_INGOT, GOLD_INGOT, GOLD_INGOT],
      [GOLD_INGOT, GOLD_INGOT, GOLD_INGOT],
    ],
    result: { id: GOLD_BLOCK, count: 1 },
  },
  {
    id: "gold_block_to_ingots",
    ingredients: [GOLD_BLOCK],
    result: { id: GOLD_INGOT, count: 9 },
  },
  {
    id: "bow",
    pattern: [
      [null, STICK, STRING],
      [STICK, null, STRING],
      [null, STICK, STRING],
    ],
    result: { id: BOW, count: 1 },
  },
  {
    id: "arrow",
    pattern: [
      [FLINT],
      [STICK],
      [FEATHER],
    ],
    result: { id: ARROW, count: 4 },
  },
  {
    id: "tnt",
    pattern: [
      [GUNPOWDER, SAND, GUNPOWDER],
      [SAND, GUNPOWDER, SAND],
      [GUNPOWDER, SAND, GUNPOWDER],
    ],
    result: { id: TNT, count: 1 },
  },
  {
    id: "cobweb",
    pattern: [
      [STRING, null, STRING],
      [null, STRING, null],
      [STRING, null, STRING],
    ],
    result: { id: COBWEB, count: 1 },
  },
  ...toolRecipes(PLANKS, "wooden", WOODEN_PICKAXE, WOODEN_AXE, WOODEN_SHOVEL, WOODEN_SWORD),
  ...toolRecipes(COBBLESTONE, "stone", STONE_PICKAXE, STONE_AXE, STONE_SHOVEL, STONE_SWORD),
  ...toolRecipes(IRON_INGOT, "iron", IRON_PICKAXE, IRON_AXE, IRON_SHOVEL, IRON_SWORD),
  ...toolRecipes(DIAMOND, "diamond", DIAMOND_PICKAXE, DIAMOND_AXE, DIAMOND_SHOVEL, DIAMOND_SWORD),
  ...armorRecipes(LEATHER, "leather", LEATHER_HELMET, LEATHER_CHESTPLATE, LEATHER_LEGGINGS, LEATHER_BOOTS),
  ...armorRecipes(IRON_INGOT, "iron", IRON_HELMET, IRON_CHESTPLATE, IRON_LEGGINGS, IRON_BOOTS),
  ...armorRecipes(DIAMOND, "diamond", DIAMOND_HELMET, DIAMOND_CHESTPLATE, DIAMOND_LEGGINGS, DIAMOND_BOOTS),
];

/**
 * The four armor shapes, generated once per material. All are 3-wide or 3-high,
 * so they fit only on a crafting table's 3x3 grid.
 */
function armorRecipes(
  material: number,
  prefix: string,
  helmet: number,
  chestplate: number,
  leggings: number,
  boots: number,
): Recipe[] {
  return [
    {
      id: `${prefix}_helmet`,
      pattern: [
        [material, material, material],
        [material, null, material],
      ],
      result: { id: helmet, count: 1 },
    },
    {
      id: `${prefix}_chestplate`,
      pattern: [
        [material, null, material],
        [material, material, material],
        [material, material, material],
      ],
      result: { id: chestplate, count: 1 },
    },
    {
      id: `${prefix}_leggings`,
      pattern: [
        [material, material, material],
        [material, null, material],
        [material, null, material],
      ],
      result: { id: leggings, count: 1 },
    },
    {
      id: `${prefix}_boots`,
      pattern: [
        [material, null, material],
        [material, null, material],
      ],
      result: { id: boots, count: 1 },
    },
  ];
}

/**
 * The four tool shapes, generated once per material. All are 3-high, so they
 * only fit on a crafting table's 3x3 grid.
 */
function toolRecipes(
  material: number,
  prefix: string,
  pickaxe: number,
  axe: number,
  shovel: number,
  sword: number,
): Recipe[] {
  return [
    {
      id: `${prefix}_sword`,
      pattern: [[material], [material], [STICK]],
      result: { id: sword, count: 1 },
    },
    {
      id: `${prefix}_pickaxe`,
      pattern: [
        [material, material, material],
        [null, STICK, null],
        [null, STICK, null],
      ],
      result: { id: pickaxe, count: 1 },
    },
    {
      id: `${prefix}_axe`,
      pattern: [
        [material, material],
        [material, STICK],
        [null, STICK],
      ],
      result: { id: axe, count: 1 },
    },
    {
      id: `${prefix}_shovel`,
      pattern: [[material], [STICK], [STICK]],
      result: { id: shovel, count: 1 },
    },
  ];
}

export function findRecipe(grid: GridView): Recipe | null {
  for (const recipe of RECIPES) {
    if (matchesRecipe(recipe, grid)) return recipe;
  }
  return null;
}

export function matchesRecipe(recipe: Recipe, grid: GridView): boolean {
  if (recipe.ingredients) return matchesShapeless(recipe.ingredients, grid);
  if (!recipe.pattern) return false;
  return matchesShaped(recipe.pattern, grid);
}

function matchesShapeless(ingredients: readonly number[], grid: GridView): boolean {
  const ids: number[] = [];
  for (const cell of grid.cells) {
    if (cell) ids.push(cell.id);
  }
  if (ids.length !== ingredients.length) return false;

  const wanted = [...ingredients].sort((a, b) => a - b);
  ids.sort((a, b) => a - b);
  for (let i = 0; i < wanted.length; i++) {
    if (wanted[i] !== ids[i]) return false;
  }
  return true;
}

function matchesShaped(
  pattern: readonly (readonly (number | null)[])[],
  grid: GridView,
): boolean {
  const trimmedPattern = trimPattern(pattern);
  const trimmedGrid = trimGrid(grid);
  if (!trimmedPattern || !trimmedGrid) return false;
  if (
    trimmedPattern.width !== trimmedGrid.width ||
    trimmedPattern.rows.length !== trimmedGrid.height
  ) {
    return false;
  }

  if (matchesRows(trimmedPattern.rows, trimmedGrid)) return true;

  // Also support horizontal mirroring (e.g. right-facing vs left-facing axe).
  const mirrored = trimmedPattern.rows.map((row) => [...row].reverse());
  return matchesRows(mirrored, trimmedGrid);
}

function matchesRows(
  rows: readonly (readonly (number | null)[])[],
  grid: TrimmedGrid,
): boolean {
  for (let y = 0; y < rows.length; y++) {
    for (let x = 0; x < rows[y].length; x++) {
      const want = rows[y][x];
      const cell = grid.cells[y * grid.width + x];
      if (want === null) {
        if (cell) return false;
      } else if (cell?.id !== want) {
        return false;
      }
    }
  }
  return true;
}

interface TrimmedPattern {
  readonly width: number;
  readonly height: number;
  readonly rows: (number | null)[][];
}

function trimPattern(
  pattern: readonly (readonly (number | null)[])[],
): TrimmedPattern | null {
  const height = pattern.length;
  const width = height > 0 ? pattern[0].length : 0;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pattern[y][x] === null || pattern[y][x] === undefined) continue;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < 0) return null;

  const rows: (number | null)[][] = [];
  for (let y = minY; y <= maxY; y++) rows.push(pattern[y].slice(minX, maxX + 1));
  return { width: maxX - minX + 1, height: maxY - minY + 1, rows };
}

interface TrimmedGrid {
  readonly width: number;
  readonly height: number;
  readonly cells: (ItemStack | null)[];
}

function trimGrid(grid: GridView): TrimmedGrid | null {
  const width = grid.width;
  const height = Math.ceil(grid.cells.length / width);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!grid.cells[y * width + x]) continue;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < 0) return null;

  const cells: (ItemStack | null)[] = [];
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) cells.push(grid.cells[y * width + x] ?? null);
  }
  return { width: maxX - minX + 1, height: maxY - minY + 1, cells };
}
